/**
 * A zod schema for one action's form, built from its `FieldSpec[]`.
 *
 * `ARCHITECTURE.md` §Frontend: "react-hook-form with the zod schema from
 * `packages/shared` as resolver; the same schema the API validates with." That rule
 * cannot be followed here and it is worth being exact about why: the schema the API
 * validates a studio action with is `apps/api`'s own `defineAction` input, and
 * `apps/web` may take **types only** from `apps/api` (lint-enforced). There is no
 * object to share.
 *
 * So this is deliberately the smallest schema that makes the form work, and the
 * server stays the authority:
 *
 *   - **required means present.** A blank required box is refused here, because the
 *     alternative is a round trip to be told what the form already knew.
 *   - **a number box holds a number.** `Input type="number"` hands back a string, and
 *     "not a number" is the one type error a reader makes constantly.
 *   - **everything else is accepted and sent.** A slug that does not exist, an amount
 *     over an allowance, a period with no run — all of those are the server's to
 *     refuse, and `ActionForm` puts its `errors[]` back on the fields.
 *
 * Rebuilding the API's own rules here would be a second validator, and the failure it
 * produces is the worst kind: a form that refuses something the server would have
 * accepted, with a message nobody wrote.
 */

import { z } from 'zod'

import { CURRENCIES } from '@fareast/shared'

import type { FieldSpec } from '../portal/schemaFields.js'
import { isBlank, type FormValues } from '../portal/schemaForm.js'

/**
 * The values half of the same idea, one folder up.
 *
 * `components/portal/schemaForm.ts` is what a `FieldSpec[]` form *holds* and what it
 * *sends*; this file is what it is *validated* by. The two were one file until the
 * client's widget configuration form needed the first half and not the second
 * (`P5-v1-1.md` item (d2)), and the names stay here so the callers that already know
 * them — `ActionField.tsx` and its test — keep their import.
 */
export { emptyRow, emptyValue, type FieldValue, type FormValues } from '../portal/schemaForm.js'

/** The schema `useZodForm` resolves this form with. */
export function schemaFor(fields: readonly FieldSpec[]): z.ZodType<FormValues, FormValues> {
  const shape: Record<string, z.ZodType> = {}
  for (const field of fields) shape[field.name] = fieldSchema(field)
  // `looseObject`: `fixed` fields are merged in by `ActionForm` after validation, and
  // a strict object here would refuse a form state react-hook-form legitimately holds
  // (an untouched row's blank keys) rather than the input the server sees.
  return z.looseObject(shape)
}

/**
 * One field's schema, required or not.
 *
 * **Optionality is per widget, not a wrapper.** An optional text box is empty when it
 * holds `''`; an optional money field is empty when its *amount box* holds `''` and
 * its currency select still holds `USD`; an optional list is empty when it is `[]`.
 * Wrapping the required schema in `z.union([z.literal(''), z.undefined(), schema])`
 * covers the first and silently refuses the other two — which showed as a form with
 * no visible error that would not submit, because the failing field was one nobody
 * had touched.
 */
function fieldSchema(field: FieldSpec): z.ZodType {
  switch (field.widget) {
    case 'checkbox':
      return z.boolean()

    case 'number':
    case 'qty':
      return numeric(field, refusal(field, `${field.label} has to be a number.`))

    case 'money':
      return z.object({
        major: numeric(field, refusal(field, `${field.label} has to be an amount.`)),
        currency: z.enum(CURRENCIES),
      })

    case 'multiselect':
      return field.optional
        ? z.array(z.string())
        : z
            .array(z.string())
            .min(1, refusal(field, `Choose at least one ${field.label.toLowerCase()}.`))

    case 'rows':
      return field.optional
        ? z.array(z.looseObject(rowShape(field)))
        : z
            .array(z.looseObject(rowShape(field)))
            .min(1, refusal(field, `${field.label} needs at least one row.`))

    // Everything else is a string in a box: text, textarea, tags (one per line),
    // date, period, ref, client, service, assignee, datetime, select and scalar.
    default:
      return field.optional ? z.string() : answered(refusal(field, blankMessage(field)))
  }
}

/**
 * A box that has to be answered, by **the form's own idea of blank**.
 *
 * It was `z.string().min(1)`, and a title replaced by a space passed it: one
 * character, so "answered" here, and blank to `wireValuesOf`, which trims — so the
 * field was left out of the body, a partial update read that as *unchanged*, the API
 * answered 200, the window closed and the title was still there. Two definitions of
 * blank, and the gap between them was a save that silently did nothing. `isBlank` is
 * the one `wireValuesOf` sends by, so a value this passes is a value that is sent.
 */
function answered(message: string): z.ZodType {
  return z.string().refine((value) => !isBlank(value), message)
}

/**
 * What a field is refused with: its own sentence, or the shape's.
 *
 * `actionCopy.ts` is the one place a human-facing refusal is written, and its own
 * header says why it has to be a whole sentence — a refusal **replaces** the hint
 * while it is showing (CONTRACT.md §2.8 gives a field one muted line), so a message
 * that only restates the field name takes the guidance away at the exact moment it is
 * needed. R3-09 found `Reason is required.` doing precisely that.
 */
function refusal(field: FieldSpec, fallback: string): string {
  return field.required ?? fallback
}

/**
 * The fallback, per widget, when nobody has written the field its own sentence.
 *
 * It says what to *do* rather than that something is missing, which is the standard —
 * and a picker is told apart from a box, because "Choose one" and "Write something"
 * are different instructions and `Client is required.` was neither.
 */
function blankMessage(field: FieldSpec): string {
  const what = field.label.toLowerCase()
  switch (field.widget) {
    case 'select':
    case 'client':
    case 'service':
    case 'assignee':
      return `Choose one — ${what} has to be answered before this can run.`
    case 'date':
    case 'datetime':
    case 'period':
      return `Pick the ${what} this is for.`
    case 'ref':
      return `Give the reference — ${what} is what this call is about.`
    case 'textarea':
      return `Write the ${what}. A sentence is enough, and it goes on the record.`
    default:
      return `${field.label} is needed before this can run.`
  }
}

/**
 * A box that holds a number, or — when the field is optional — nothing.
 *
 * `Input type="number"` hands back a string either way, so this is a string schema
 * with a predicate rather than `z.coerce.number()`: coercion turns `''` into `0`,
 * and `0` is a value somebody chose. Min/max from the published schema (when the
 * field stayed a free number box) refuse values the spinner could still type.
 */
function numeric(field: FieldSpec, message: string): z.ZodType {
  return z.string().refine((value) => {
    const trimmed = value.trim()
    if (trimmed === '') return field.optional
    const n = Number(trimmed)
    if (Number.isNaN(n)) return false
    if (field.min !== undefined && n < field.min) return false
    if (field.max !== undefined && n > field.max) return false
    return true
  }, message)
}

function rowShape(field: FieldSpec): Record<string, z.ZodType> {
  const shape: Record<string, z.ZodType> = {}
  for (const inner of field.fields ?? []) shape[inner.name] = fieldSchema(inner)
  return shape
}
